import { useEffect, useLayoutEffect, useRef, useState, memo } from "react";
import { pdfjsLib } from "../../../lib/pdf";
import { TextLayer } from "pdfjs-dist";
import "./PdfViewer.css";

// ── Text-layer highlight helper ───────────────────────────────────────
// Strips whitespace before matching so PyMuPDF extraction and PDF.js
// rendering differences (ligatures, extra spaces) don't break lookup.
// `anchorY` (px, page-relative) is the vertical centre of the citation's bbox
// when one exists: table pages repeat cell texts ("Lagerschaden", "Lager
// auswechseln", "falsche Gurtspannung" all occur twice on REMA S.44), and the
// first occurrence is the wrong row half the time. With an anchor, the
// occurrence nearest to the bbox wins; without one, the first still does.
function applyTextHighlight(container, highlightText, anchorY = null) {
  if (!highlightText || !container) return;
  const spans = Array.from(container.querySelectorAll('span'))
    .filter(s => !s.children.length && s.textContent.trim().length > 0);
  if (!spans.length) return;

  const parts = [];
  let pos = 0;
  for (const span of spans) {
    const len = span.textContent.length;
    parts.push({ span, start: pos, end: pos + len });
    pos += len;
  }
  const fullText = spans.map(s => s.textContent).join('');

  const stripWS = s => s.replace(/\s+/g, '');
  const haystackStripped = stripWS(fullText);
  const fullNeedleLen = stripWS(highlightText).length;

  // Map stripped positions back to original fullText positions
  const origMap = [];
  for (let i = 0; i < fullText.length; i++) {
    if (!/\s/.test(fullText[i])) origMap.push(i);
  }
  const spanTopAt = (strippedIdx) => {
    const o = origMap[strippedIdx] ?? 0;
    const hit = parts.find(({ start, end }) => end > o && start < o + 1);
    return hit ? hit.span.offsetTop : null;
  };

  // Full needle first, then progressively shorter prefixes — multi-column
  // PDFs and page-number injections by PDF.js can break longer matches.
  let sIdx = -1;
  for (const prefixLen of [Infinity, 120, 60, 30, 10]) {
    const needle = stripWS(highlightText.slice(0, prefixLen));
    if (needle.length < 6) break;
    const occurrences = [];
    for (let i = haystackStripped.indexOf(needle); i !== -1 && occurrences.length < 50;
         i = haystackStripped.indexOf(needle, i + 1)) {
      occurrences.push(i);
    }
    if (!occurrences.length) continue;
    sIdx = occurrences[0];
    if (occurrences.length > 1 && anchorY != null) {
      let best = Infinity;
      for (const idx of occurrences) {
        const top = spanTopAt(idx);
        if (top == null) continue;
        const d = Math.abs(top - anchorY);
        if (d < best) { best = d; sIdx = idx; }
      }
    }
    break;
  }
  if (sIdx === -1) return;

  const origStart = origMap[sIdx] ?? 0;
  const origEndIdx = sIdx + fullNeedleLen;
  const origEnd = origEndIdx < origMap.length ? origMap[origEndIdx] : fullText.length;

  for (const { span, start, end } of parts) {
    if (end > origStart && start < origEnd) {
      span.classList.add('pdf-text-highlight');
    }
  }
}

// ── Single page component ─────────────────────────────────────────────
// Highlights are rendered declaratively in JSX — React reconciles the
// overlay on every prop change, so multiple same-page citation clicks
// always reflect the newest bbox without imperative DOM mutation,
// timing guards, or scale-render races.
const PdfPage = memo(function PdfPage({ pdf, pageNum, scale, pageWidth, pageHeight, highlightBboxes, highlightText }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const renderedScaleRef = useRef(null);

  const width = pageWidth * scale;
  const height = pageHeight * scale;

  // Render when visible via IntersectionObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    renderedScaleRef.current = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && renderedScaleRef.current !== scale) {
          renderedScaleRef.current = scale;
          renderPageContent();
        }
      },
      { rootMargin: "400px 0px" }
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [scale, highlightText]);

  async function renderPageContent() {
    const pdfPage = await pdf.getPage(pageNum);
    const viewport = pdfPage.getViewport({ scale });

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    if (renderTaskRef.current) renderTaskRef.current.cancel();
    const task = pdfPage.render({ canvasContext: ctx, viewport });
    renderTaskRef.current = task;

    try {
      await task.promise;
    } catch (e) {
      if (e.name === "RenderingCancelledException") return;
    }

    // Text layer (for text selection, not highlighting)
    const textDiv = textLayerRef.current;
    if (textDiv) {
      textDiv.innerHTML = "";
      textDiv.style.setProperty("--total-scale-factor", `${viewport.scale}`);
      textDiv.style.setProperty("--scale-round-x", "1px");
      textDiv.style.setProperty("--scale-round-y", "1px");
      const textContent = await pdfPage.getTextContent();
      const tl = new TextLayer({ textContentSource: textContent, container: textDiv, viewport });
      await tl.render();
      textDiv.style.width = `${viewport.width}px`;
      textDiv.style.height = `${viewport.height}px`;
      if (highlightText) {
        const anchor = (highlightBboxes || []).find(b => Number(b.page) === pageNum);
        const anchorY = anchor
          ? ((Number(anchor.y0) + Number(anchor.y1)) / 2) * viewport.height
          : null;
        applyTextHighlight(textDiv, highlightText, anchorY);
      }
    }
  }

  const pageBboxes = (highlightBboxes || []).filter(b => Number(b.page) === pageNum);

  return (
    <div
      ref={containerRef}
      className="pdf-page-wrapper"
      data-page={pageNum}
      style={{ width, height }}
    >
      <canvas ref={canvasRef} />
      <div ref={textLayerRef} className="textLayer" />
      <div className="pdf-highlight-layer" style={{ width, height }}>
        {pageBboxes.map((b, i) => (
          <div
            key={`${b.x0}-${b.y0}-${b.x1}-${b.y1}-${i}`}
            className="pdf-highlight"
            style={{
              left:   `${b.x0 * width}px`,
              top:    `${b.y0 * height}px`,
              width:  `${(b.x1 - b.x0) * width}px`,
              height: `${(b.y1 - b.y0) * height}px`,
            }}
          />
        ))}
      </div>
    </div>
  );
});

// ── Main viewer ───────────────────────────────────────────────────────
export default function PdfViewer({ url, page = 1, highlightBboxes = [], highlightText = '', displayName, onClose }) {
  const pdfDocRef = useRef(null);
  const scrollRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(page);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageInputValue, setPageInputValue] = useState(String(page));
  const [pageDims, setPageDims] = useState(null);
  const scrollingToRef = useRef(false);
  const prevScaleRef = useRef(scale);

  // Load document + get page 1 dimensions
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let currentLoadingTask = null;

    setLoading(true);
    setError(null);
    setNumPages(0);
    setPageDims(null);

    async function loadPdf(attempt = 1) {
      try {
        currentLoadingTask = pdfjsLib.getDocument({
          url,
          withCredentials: true,
          disableAutoFetch: false,
        });

        const pdf = await currentLoadingTask.promise;
        if (cancelled) { pdf.destroy(); return; }

        pdfDocRef.current = pdf;
        const p1 = await pdf.getPage(1);
        if (cancelled) return;

        const vp = p1.getViewport({ scale: 1 });
        setNumPages(pdf.numPages);
        setPageDims({ width: vp.width, height: vp.height });
        // Schmale Container (Mobile): initial auf Containerbreite einpassen —
        // nur verkleinern, nie vergrößern; Zoom-Buttons bleiben nutzbar.
        const cw = scrollRef.current?.clientWidth;
        if (cw && cw < vp.width + 24) {
          setScale(Math.max(0.3, Math.floor(((cw - 16) / vp.width) * 100) / 100));
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (attempt < 2) {
          console.warn(`PDF load attempt ${attempt} failed, retrying...`, err.message);
          await new Promise(r => setTimeout(r, 500));
          if (!cancelled) loadPdf(attempt + 1);
        } else {
          console.error("PDF load failed:", err);
          setError("PDF konnte nicht geladen werden.");
          setLoading(false);
        }
      }
    }

    loadPdf();

    return () => {
      cancelled = true;
      currentLoadingTask?.destroy?.();
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [url]);

  // Scroll to target page after load. First scroll after a fresh load is
  // instant (avoid a long animation from page 1 → target); subsequent `page`
  // changes (e.g. user clicks another citation for the same doc) animate
  // smoothly from the currently-visible page.
  const hasInitialScrolledRef = useRef(false);
  useEffect(() => {
    // Reset the initial-scroll flag whenever the document reloads
    hasInitialScrolledRef.current = false;
  }, [url]);

  useEffect(() => {
    if (!loading && numPages > 0 && page >= 1 && pageDims) {
      const smooth = hasInitialScrolledRef.current;
      const timer = setTimeout(() => {
        scrollToPage(page, { smooth });
        hasInitialScrolledRef.current = true;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, numPages, pageDims, page]);

  // Preserve scroll position when zooming
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container || prevScaleRef.current === scale) return;
    const ratio = scale / prevScaleRef.current;
    container.scrollTop *= ratio;
    container.scrollLeft *= ratio;
    prevScaleRef.current = scale;
  }, [scale]);

  // Track current page from scroll position
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !pageDims) return;

    const pageH = pageDims.height * scale + 8;

    const onScroll = () => {
      if (scrollingToRef.current) return;
      const scrollTop = container.scrollTop + container.offsetHeight / 3;
      const pg = Math.min(numPages, Math.max(1, Math.floor(scrollTop / pageH) + 1));
      if (pg !== currentPage) {
        setCurrentPage(pg);
        setPageInputValue(String(pg));
      }
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [currentPage, numPages, pageDims, scale]);

  function scrollToPage(num, { smooth = false } = {}) {
    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector(`[data-page="${num}"]`);
    if (target) {
      scrollingToRef.current = true;
      // Distance based on actual scroll position, not currentPage — which
      // may already be the target (e.g. viewer just opened with page=N).
      const pageH = (pageDims?.height || 0) * scale + 8;
      const visiblePage = pageH > 0 ? Math.floor(container.scrollTop / pageH) + 1 : 1;
      const distance = Math.abs(num - visiblePage);
      const useSmooth = smooth && distance <= 5;
      target.scrollIntoView({ behavior: useSmooth ? "smooth" : "instant", block: "start" });
      setTimeout(() => { scrollingToRef.current = false; }, useSmooth ? 600 : 100);
    }
    setCurrentPage(num);
    setPageInputValue(String(num));
  }

  const goToPage = (num) => scrollToPage(Math.max(1, Math.min(numPages, num)), { smooth: true });

  const handlePageInputKeyDown = (e) => {
    if (e.key === "Enter") {
      const num = parseInt(pageInputValue, 10);
      if (!isNaN(num)) goToPage(num);
    }
  };

  return (
    <div className="pdf-viewer">
      <div className="pdf-viewer-toolbar">
        <span className="pdf-toolbar-title" title={displayName || ""}>{displayName || ""}</span>
        <div className="pdf-toolbar-controls">
          <button className="pdf-toolbar-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} title="Vorherige Seite">‹</button>
          <div className="pdf-page-indicator">
            <input
              type="text"
              className="pdf-page-input"
              value={pageInputValue}
              onChange={(e) => setPageInputValue(e.target.value)}
              onKeyDown={handlePageInputKeyDown}
              onBlur={() => setPageInputValue(String(currentPage))}
            />
            <span className="pdf-page-total">/ {numPages}</span>
          </div>
          <button className="pdf-toolbar-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= numPages} title="Nächste Seite">›</button>
          <div className="pdf-toolbar-separator" />
          <button className="pdf-toolbar-btn" onClick={() => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(1)))} title="Verkleinern">−</button>
          <span className="pdf-zoom-label">{Math.round(scale * 100)}%</span>
          <button className="pdf-toolbar-btn" onClick={() => setScale((s) => Math.min(3, +(s + 0.2).toFixed(1)))} title="Vergrößern">+</button>
        </div>
        {onClose && (
          <button className="pdf-toolbar-close" onClick={onClose} title="Schließen">✕</button>
        )}
      </div>

      <div className="pdf-viewer-scroll" ref={scrollRef}>
        {loading && <div className="pdf-viewer-loading">Lade PDF...</div>}
        {error && <div className="pdf-viewer-error">{error}</div>}
        {!loading && !error && pdfDocRef.current && pageDims &&
          Array.from({ length: numPages }, (_, i) => (
            <PdfPage
              key={`${url}-p${i + 1}`}
              pdf={pdfDocRef.current}
              pageNum={i + 1}
              scale={scale}
              pageWidth={pageDims.width}
              pageHeight={pageDims.height}
              highlightBboxes={highlightBboxes}
              highlightText={i + 1 === page ? highlightText : ''}
            />
          ))
        }
      </div>
    </div>
  );
}
